<template>
  <div class="hero" :class="[`hero--${component.heroVariant}`, { 'hero--has-content': hasContent }]">
    <SharedPicture
      :media="component.heroPicture"
      :alt="component.heroPicture?.alternativeText || ''"
      class="hero__picture !hidden lg:!block"
      height="600"
      width="1680"
      format="webp"
      :quality="85"
      :fetchpriority="index === 0 ? 'high' : 'auto'"
      :loading="index === 0 ? 'eager' : 'lazy'"
    />
    <SharedPicture
      :media="component.heroMobilePicture || component.heroPicture"
      class="hero__picture !block lg:!hidden"
      height="350"
      width="800"
      format="webp"
      :quality="85"
      :fetchpriority="index === 0 ? 'high' : 'auto'"
      :loading="index === 0 ? 'eager' : 'lazy'"
    />
    <div v-if="hasContent" class="hero__overlay absolute w-full h-full bg-primary/80 z-10"></div>
    <template v-if="hasContent && component.heroVariant === 'normal'">
      <div
        class="hero__content z-20 relative container container--page-content py-16 lg:py-20 flex gap-4 flex-col lg:flex-row lg:items-end lg:justify-between"
        v-if="hasContent"
      >
        <div class="hero__content-inner w-full lg:max-w-[65%]">
          <p class="hero__subtitle text-regular-normal !text-white mb-4" v-if="component.heroSubtitle">
            {{ component.heroSubtitle }}
          </p>
          <div
            class="hero__title-container text-white mb-6"
            v-if="component.heroTitle"
            v-html="component.heroTitle"
          ></div>
          <p class="hero__text text-medium-normal !text-white" v-if="component.heroText">
            {{ component.heroText }}
          </p>
        </div>
        <div class="hero__ctas flex flex-wrap gap-4" v-if="component.heroCtas?.length">
          <PageComponentsCtas :component="{ __typename: 'ComponentPageComponentsCTAs', ctas: component.heroCtas }" />
        </div>
      </div>
    </template>
    <template v-if="hasContent && component.heroVariant === 'big'">
      <div
        class="hero__content-inner container container--page-content relative z-20 mt-auto mb-9 !max-w-[90rem] lg:mb-20"
      >
        <div class="p-6 bg-primary/80 rounded-sm flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div
            class="hero__title-container text-white lg:max-w-[45%]"
            v-if="component.heroTitle"
            v-html="component.heroTitle"
          ></div>
          <div class="flex flex-col gap-4 lg:max-w-[50%] lg:pt-6">
            <p class="hero__text text-medium-normal !text-white" v-if="component.heroText">
              {{ component.heroText }}
            </p>

            <div class="hero__ctas flex flex-wrap gap-4" v-if="component.heroCtas?.length">
              <PageComponentsCtas
                :component="{ __typename: 'ComponentPageComponentsCTAs', ctas: component.heroCtas }"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import type { GraphQLHero } from '~/types/graphql';

  const props = defineProps<{
    component: GraphQLHero;
    index: number;
  }>();

  const hasContent =
    props.component.heroTitle ||
    props.component.heroSubtitle ||
    props.component.heroText ||
    props.component.heroCtas?.length;
</script>

<style lang="scss" scoped>
  .hero {
    @apply relative w-full h-auto min-h-[440px];
    @apply z-0;
    @apply flex flex-col justify-center;

    &__picture {
      @apply absolute w-full h-full object-cover object-center;
      @apply top-0 left-0;
      @apply z-10;
    }

    &--big {
      height: 100vh;
      min-height: 300px;
      max-height:346px;

      @screen lg {
        min-height: 450px;
        max-height: 600px;
      }

      .hero {
        &__overlay {
          @apply hidden;
        }

        &__content {
          @apply bottom-9 mt-auto;
        }
      }
    }

    &--has-content.hero--big {
      @apply min-h-[616px];

      @screen lg {
        min-height: calc(100vh - var(--header-height, 90px));
        max-height: calc(900px - var(--header-height, 90px));
      }
    }
  }
</style>
